/**
 * SectionJumpBar — sticky in-page section navigation, drawn as Carbon line
 * tabs (DESIGN.md "Navigation"): 48px links over a 2px border-subtle
 * baseline; the section in view is 600 with a 2px interactive-line underline
 * and `aria-current="location"`. Counts render as 18px tags. The strip is its
 * own horizontal scroller, so it never widens the page on a phone.
 *
 * `accent='deep'` marks a pivot to a different surface (the deep-dive tabs):
 * it carries a trailing arrow so it reads as a destination, not one more
 * in-page anchor.
 *
 * Each item is a real `<a href="#id">` so keyboard / screen-reader users get
 * native focus + hash navigation. The click handler intercepts to preserve a
 * clean `history.replaceState` (no back-button spam) and to honour
 * `prefers-reduced-motion` — users who opted out of motion get an instant
 * jump instead of a smooth scroll.
 *
 * On mount we also check `location.hash`. If it matches a section id we
 * scroll to it under the same reduced-motion rule. This makes
 * `/security?tab=best-practices#frameworks`-style deep links work even when
 * the section isn't above the fold on initial paint.
 *
 * Pinned state: the bar self-detects when it has pinned via an
 * IntersectionObserver on itself (rootMargin shrinks the root from the top by
 * the bar's sticky offset so its intersection ratio drops below 1 the moment
 * it sticks). The bar pins flush beneath the page's sticky tab strip rather
 * than at the top of <main> — the strip publishes its height as the
 * `--pillar-tabs-h` CSS variable, which the bar resolves into its `top` inset
 * (falling back to 0 where no strip is present), so the two stack as one
 * sticky header instead of colliding. When pinned, the bar swaps its "Jump
 * to" lead for the optional `summary` prop, so the reader keeps the hero's
 * context after it scrolls away. No shadow (Layer-Not-Shadow Rule): the bar
 * sits on the ground color with its baseline rule.
 *
 * The component does not own the section anchors — the parent is responsible
 * for rendering `<section id="...">` elements that match the `sections`
 * prop's `id` values.
 */

import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useActiveSection } from '@/hooks/useActiveSection'
import { IconArrowRight } from '@/components/carbon/icons'
import { Tag } from '@/components/carbon/Tag'

export interface SectionJumpItem {
  /** Anchor target. Must match a `<section id>` rendered by the parent. */
  id: string
  /** Item label, in sentence case. */
  label: string
  /** Count tag. `null`, `undefined`, or `0` hides it. */
  badge?: number | null
  /** Destination accent — a trailing arrow signals a pivot-style jump
   *  (not an in-page anchor). */
  accent?: 'deep'
}

export interface SectionJumpBarProps {
  sections: SectionJumpItem[]
  /** Defaults to "Jump to". */
  leadLabel?: string
  /** Accessible name for the nav landmark. Defaults to "Section jump". */
  ariaLabel?: string
  /** Optional content that replaces the lead label when the bar is pinned.
   *  Use it to surface a compact summary of the hero that just scrolled off
   *  (e.g. "Best Practice 87%") so the user keeps context. When omitted, the
   *  lead label stays in place. */
  summary?: React.ReactNode
}

/**
 * Resolve whether the user has opted into reduced motion. Wrapped in a
 * function so the lookup happens at click-time, not mount-time — users can
 * flip the OS setting mid-session.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const ITEM =
  'inline-flex h-12 flex-none items-center gap-2 whitespace-nowrap border-b-2 px-4 text-body ' +
  'transition-colors duration-110 ease-carbon'

export function SectionJumpBar({
  sections,
  leadLabel,
  ariaLabel,
  summary,
}: SectionJumpBarProps) {
  const activeId = useActiveSection(sections.map((s) => s.id))
  const navRef = useRef<HTMLElement>(null)
  const [isPinned, setIsPinned] = useState(false)

  // Mount-time hash handling. If the URL arrived with `#section-id` we scroll
  // there now. The native browser behaviour would have fired before our
  // sticky bar mounted, so the section would have ended up partially obscured.
  // Re-running the scroll once we're mounted gives the `scroll-mt-*`
  // utilities on `<section>` a chance to do their job.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const hash = window.location.hash.slice(1)
    if (!hash) return
    if (!sections.some((s) => s.id === hash)) return
    const el = document.getElementById(hash)
    if (!el) return
    el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    // Run once on mount. We deliberately don't depend on `sections` because
    // re-running this on every list-identity churn would yank the user back
    // to the hash mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Detect "pinned" state by watching the bar against the scroll container
  // (`<main>`), shrunk from the top by the bar's own sticky offset. The bar
  // pins flush beneath the page's sticky tab strip, whose height is published
  // as `--pillar-tabs-h` and resolved into our `top` inset — so within the
  // scroll container the pin line is at `stickyTop`, not at the container top.
  // We read that offset back from the computed `top` and shrink the root to
  // match: once the bar reaches its pin line, 1px of it crosses the shrunk
  // root edge and its intersectionRatio drops below 1. The
  // boundingClientRect.top guard avoids a false positive when the bar starts
  // below the fold. We re-read on resize since the tab strip reflows.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return
    const nav = navRef.current
    if (!nav) return
    const scrollRoot = nav.closest('main')

    let observer: IntersectionObserver | undefined
    const setup = () => {
      observer?.disconnect()
      const stickyTop = parseFloat(getComputedStyle(nav).top) || 0
      observer = new IntersectionObserver(
        ([entry]) => {
          const rootTop = entry.rootBounds?.top ?? 0
          setIsPinned(
            entry.intersectionRatio < 1 &&
              entry.boundingClientRect.top <= rootTop + stickyTop + 1,
          )
        },
        { root: scrollRoot, threshold: [1], rootMargin: `-${stickyTop + 1}px 0px 0px 0px` },
      )
      observer.observe(nav)
    }

    setup()
    window.addEventListener('resize', setup)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', setup)
    }
  }, [])

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    e.preventDefault()
    // `replaceState` keeps the hash addressable for share / refresh without
    // polluting back-button history with one entry per click.
    history.replaceState(null, '', `#${id}`)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      })
    }
  }

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel ?? 'Section jump'}
      data-pinned={isPinned ? 'true' : 'false'}
      // Pins flush beneath the page's sticky tab strip (its height is published
      // as `--pillar-tabs-h`); `z-10` keeps it under the strip's `z-20`.
      className="sticky top-[var(--pillar-tabs-h)] z-10 flex min-w-0 items-center bg-[color:var(--background)]"
    >
      <span className="hidden flex-none items-center pr-2 text-helper text-text-helper c-md:flex">
        {isPinned && summary != null ? summary : leadLabel ?? 'Jump to'}
      </span>
      <div className="relative min-w-0 flex-1">
        {/* The 2px baseline the items' underlines sit on. */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-line-subtle" />
        <div className="relative flex min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {sections.map((item) => {
            const isActive = activeId === item.id
            const showBadge = item.badge != null && item.badge > 0
            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                aria-current={isActive ? 'location' : undefined}
                aria-label={showBadge ? `${item.label}, ${item.badge}` : undefined}
                className={clsx(
                  ITEM,
                  isActive
                    ? 'border-interactive-line font-semibold text-text-primary'
                    : 'border-transparent text-text-secondary hover:border-line-strong hover:text-text-primary',
                )}
              >
                {item.label}
                {showBadge && (
                  <Tag size="sm" className="tabular-nums">
                    {String(item.badge)}
                  </Tag>
                )}
                {item.accent === 'deep' && <IconArrowRight className="text-[color:var(--icon-secondary)]" />}
              </a>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
